import { Link } from 'react-router-dom';
import Alias from 'components/controls/alias';
import AccountTypes from 'context/accountTypes';
import Navigation from 'context/nav';
import Utils from 'context/utils';

import './userIdentity.css';

/**
 * UserIdentity — the one way to render a principal.
 *
 * A human shows a name and the teal alias pill. A service account has no alias at all, so it shows a
 * name and a short monospace id with the full id on its title — the id is its only handle, which is
 * why it is rendered rather than hidden. The type glyph comes from the account-type registry, so a
 * future kind of principal appears here without touching this file.
 *
 * Before this component the four sites that render a user each reached for `alias` directly, which
 * printed the literal string "undefined" beside a link to /p/undefined for anything an account
 * authored.
 *
 * The pill needs `name`, not just `alias`. `Utils.displayName` falls back to the alias when there is
 * no name — a human whose profile carries only an alias, which the profile form permits — and in that
 * position the alias IS the name. Repeating it as a pill would print it twice, and `controls/alias`
 * calls out that exact case as the one place its pill must not be used.
 *
 * `hideAccountId` suppresses only the account's short id, leaving a human's alias pill untouched —
 * narrower than `showIdentifier`, which drops both. Defaults to `false` (today's behaviour: an
 * account's id renders) so no existing call site changes; the account's profile header is the one
 * place that opts in, since the id is still reachable there via Settings → Profile.
 */
export default function UserIdentity({ user, fallback, link = true, showIdentifier = true, hideAccountId = false }) {
  const name = Utils.displayName(user, fallback);
  if ( !name ) {
    return null;
  }

  const Glyph = AccountTypes.of(user).glyph;
  const target = user ? (user.alias || user.id) : null;
  const label = (link && target)
    ? <Link to={Navigation.userProfilePath(target)}>{name}</Link>
    : <span>{name}</span>;

  return (
    <span className="bh-user-identity">
      <Glyph/>
      &nbsp;<strong>{label}</strong>
      {showIdentifier && user && AccountTypes.isHuman(user) && user.name && user.alias &&
        <>&nbsp;&nbsp;<Alias value={user.alias}/></>
      }
      {showIdentifier && !hideAccountId && user && AccountTypes.isAccount(user) && user.id &&
        <>&nbsp;&nbsp;<code title={user.id}>{Utils.shortId(user.id)}</code></>
      }
    </span>
  );
}
